import {
  eventMappingSchema,
  messageLogEntrySchema,
  messageLogPageSchema,
  messagePreviewSchema,
  messageTemplateListSchema,
  messagingConnectionSchema,
  messagingSettingsSchema,
  orderMessagePreviewSchema,
  orderMessagesSchema,
  type EventMapping,
  type MessageLogEntry,
  type MessageLogPage,
  type MessagePreview,
  type MessageStatus,
  type MessageTemplateList,
  type MessagingConnection,
  type MessagingSettings,
  type NotificationEvent,
  type OrderMessagePreview,
  type OrderMessages,
  type SendOrderMessageInput,
  type TestSendInput,
  type UpdateEventMappingInput,
  type UpdateMessagingConnectionInput,
  type UpdateMessagingSettingsInput,
} from '@tailoring-crm/shared';
import type { ApiClient } from '../api/client.js';

/**
 * Every messaging call the app makes, validated against the same shared schemas the server
 * validated with.
 *
 * `saveConnection` is the only function here that sends a secret, and it is one-way by design:
 * the API key goes up and the response — like every other response in this file — has nowhere
 * to put one. Nothing in the app ever holds the shop's key after the form is submitted.
 */

export async function getMessagingSettings(client: ApiClient): Promise<MessagingSettings> {
  return await client.get('/notifications', { schema: messagingSettingsSchema });
}

export async function saveConnection(
  client: ApiClient,
  input: UpdateMessagingConnectionInput,
): Promise<MessagingConnection> {
  return await client.put('/notifications/connection', input, {
    schema: messagingConnectionSchema,
  });
}

export async function testConnection(client: ApiClient): Promise<MessagingConnection> {
  return await client.post('/notifications/connection/test', undefined, {
    schema: messagingConnectionSchema,
  });
}

export async function disconnectWorkspace(client: ApiClient): Promise<MessagingConnection> {
  return await client.del('/notifications/connection', undefined, {
    schema: messagingConnectionSchema,
  });
}

export async function listTemplates(client: ApiClient): Promise<MessageTemplateList> {
  return await client.get('/notifications/templates', { schema: messageTemplateListSchema });
}

export async function saveMessagingPreferences(
  client: ApiClient,
  patch: UpdateMessagingSettingsInput,
): Promise<MessagingSettings> {
  return await client.patch('/notifications', patch, { schema: messagingSettingsSchema });
}

export async function saveEventMapping(
  client: ApiClient,
  event: NotificationEvent,
  patch: UpdateEventMappingInput,
): Promise<EventMapping> {
  return await client.patch(`/notifications/events/${event}`, patch, {
    schema: eventMappingSchema,
  });
}

export async function previewEvent(
  client: ApiClient,
  event: NotificationEvent,
): Promise<MessagePreview> {
  return await client.get(`/notifications/events/${event}/preview`, {
    schema: messagePreviewSchema,
  });
}

export async function sendTestMessage(
  client: ApiClient,
  input: TestSendInput,
): Promise<MessageLogEntry> {
  return await client.post('/notifications/test-send', input, { schema: messageLogEntrySchema });
}

export interface MessageLogParams {
  status?: MessageStatus;
  event?: NotificationEvent;
  q?: string;
  page?: number;
  /**
   * How many rows to bring back. The log screen wants its page of 25; a screen that only wants
   * the COUNT the page carries asks for one, and pays for one row instead of twenty-five.
   */
  pageSize?: number;
}

export async function listMessages(
  client: ApiClient,
  params: MessageLogParams = {},
): Promise<MessageLogPage> {
  return await client.get('/notifications/log', {
    schema: messageLogPageSchema,
    query: {
      ...(params.status ? { status: params.status } : {}),
      ...(params.event ? { event: params.event } : {}),
      ...(params.q ? { q: params.q } : {}),
      page: params.page ?? 1,
      pageSize: params.pageSize ?? 25,
    },
  });
}

export async function retryMessage(client: ApiClient, messageId: string): Promise<MessageLogEntry> {
  return await client.post(`/notifications/log/${messageId}/retry`, undefined, {
    schema: messageLogEntrySchema,
  });
}

// --- One order's messages (task 15) ---------------------------------------------------------

/**
 * The order screen's three calls. They live in this file rather than beside the order calls
 * because what they return is a MESSAGE — and the schemas, the vocabulary and the rule that no
 * provider field name escapes the adapter all belong here.
 */

export async function getOrderMessages(client: ApiClient, orderId: string): Promise<OrderMessages> {
  return await client.get(`/orders/${orderId}/messages`, { schema: orderMessagesSchema });
}

export async function previewOrderMessage(
  client: ApiClient,
  orderId: string,
  event: NotificationEvent,
): Promise<OrderMessagePreview> {
  return await client.get(`/orders/${orderId}/messages/preview?event=${event}`, {
    schema: orderMessagePreviewSchema,
  });
}

export async function sendOrderMessage(
  client: ApiClient,
  orderId: string,
  event: NotificationEvent,
): Promise<MessageLogEntry> {
  return await client.post(
    `/orders/${orderId}/messages`,
    { event } satisfies SendOrderMessageInput,
    { schema: messageLogEntrySchema },
  );
}
